'use client'

import { useHydration } from '@/hooks/use-hydration'
import { useIsDesktop } from '@/hooks/use-screen'
import { useState } from 'react'

import { ChevronRoundedIcon } from '@/components/atoms/icons'
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/atoms/ui/accordion'
import { Button } from '@/components/atoms/ui/button'
import Container from '@/components/templates/container/container'

interface LayananStepProps {
  title: string
  description: string
  data: PMA['items'][0]['fields']['steps']
}

export const LayananStep = ({ title, description, data }: LayananStepProps) => {
  const isDesktop = useIsDesktop()
  const isHydrated = useHydration()
  const [show, setShow] = useState(isDesktop ? data.length : 3)

  const handleLoad = () => {
    if (show >= data.length) {
      setShow(3)
    } else {
      setShow(show + data.length - 3)
    }
  }

  if (isHydrated) {
    return (
      <section className="bg-white py-9 lg:py-16">
        <Container className="space-y-4 text-center lg:space-y-20">
          <div className="space-y-4 max-w-[824px] mx-auto">
            <h2 className="text-2xl font-extrabold text-primary-blue lg:text-4xl">{title}</h2>
            <p className="text-sm lg:text-base" dangerouslySetInnerHTML={{ __html: description }} />
          </div>

          <Accordion type="single" collapsible className="max-w-[1010px] mx-auto">
            {data.slice(0, show).map((item, index) => (
              <AccordionItem
                key={index}
                value={index.toString()}
                className="relative py-2 border-l-[3px] border-[#D1D5DB] pl-6 lg:pl-12"
              >
                <div className="bg-blue-50 rounded-b-2xl rounded-r-2xl">
                  <div className="absolute top-0 rounded-full size-6 bg-secondary-yellow -left-3.5 border-4 border-white" />
                  <AccordionTrigger
                    customArrow={<ChevronRoundedIcon className="size-7" />}
                    className="w-10/12 gap-6 px-6 py-4 text-lg data-[state=open]:pb-2.5 font-extrabold lg:px-8 lg:py-6 lg:text-xl text-primary-blue text-start"
                  >
                    <span className="w-11/12">
                      {index + 1}. {item.title}
                    </span>
                  </AccordionTrigger>
                  <AccordionContent className="px-6 pb-4 text-sm lg:px-8 lg:pb-6 lg:text-base text-start">
                    <p dangerouslySetInnerHTML={{ __html: item.description }} />
                  </AccordionContent>
                </div>
              </AccordionItem>
            ))}
          </Accordion>

          <div className="flex justify-center py-2 lg:hidden">
            <Button
              onClick={handleLoad}
              variant="outline"
              className="mx-auto font-semibold border-primary-blue text-primary-blue hover:bg-primary-blue hover:text-white"
            >
              {show >= data.length ? 'Show Less' : 'Show More'}
            </Button>
          </div>
        </Container>
      </section>
    )
  }

  return null
}
